/* eslint-disable prefer-destructuring */
/**
 * Provides utilities to interact with the Elastic APM RUM agent, and create custom transactions and spans
 *
 * {@include ../lib/apm/README.md}
 * @category Core Modules
 * @module apm
 * @packageDocumentation
 */
import { o_util } from "@gr-common/common/namespaces";
import { otto } from "@gr-exports/common/nexus";
import type { OttoNexus } from "./nexus.js";
import type { GlobalUtils } from "./index.js";

export type { ApmAsync } from "../lib/apm/index.js";
export type { SendCustomErrorLabels as SendLogLabels } from "../lib/apm/async//sendCustomError.js";

/**
 * Asyncronous functions to interact with the Elastic APM RUM agent. These function will resolve
 * after dom content loaded event is fired.
 */
export type ApmRum = OttoNexus["apm"];

/**
 * Asyncronosus namespace providing functionality for the Elastic APM RUM agent
 */
export const apm: ApmRum = otto.apm ?? {};
export const agent: ApmRum["agent"] = apm.agent;

/**
 * Returns boolean indicating if the APM agent is enabled or not.
 * Can be used to conditionaly run code that depends on the APM agent being enabled.
 */
export const isActive: ApmRum["isActive"] = apm.isActive;

/**
 * Returns the current transaction if there is one, otherwise returns null.
 * This should not be used by the service directly.
 * And should only be used for testing end evaluation purposes.
 */
export const getCurrentTransaction: ApmRum["getCurrentTransaction"] = apm.getCurrentTransaction;
export const startSpan: ApmRum["startSpan"] = apm.startSpan;
export const captureError: ApmRum["captureError"] = apm.captureError;
export const mark: ApmRum["mark"] = apm.mark;

export const addLabels: ApmRum["addLabels"] = apm.addLabels;
export const sendCustomError: ApmRum["sendCustomError"] = apm.sendCustomError;

/**
 * Synchronous functions to interact with the Elastic APM RUM.
 */
export type ApmSync = GlobalUtils["apm"];

/**
 * Synchronously provided function of APM RUM module.
 */
export const apmSync: ApmSync = o_util.apm ?? {};

export const runWithSpan = apmSync.runWithSpan;

export const withSpan = apmSync.withSpan;
